import { ApiSearchResponse, ApiOrderDetailsResponse, ApiVoucherResponse, Order, OrderDetailsData, OrderItem, VoucherInfo } from '../types';
import { determineOrderStatus } from '../utils/orderStatus';
import axios, { AxiosError, AxiosInstance } from 'axios';

export const searchOrders = async (searchTerm: string): Promise<Order[]> => {
  try {
    const response:any = await axios.get(`/infrapay/search?value=${encodeURIComponent(searchTerm)}`);
    if (!response.data.success) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const apiResponse: ApiSearchResponse = response.data.data;
    
    if (!apiResponse.success) {
      throw new Error('API returned unsuccessful response');
    }

    const { columns, data } = response.data.data;

    const idPedidoIndex = columns.indexOf('IDPedido');
    const razaoSocialIndex = columns.indexOf('RazaoSocial');
    const dataIndex = columns.indexOf('Data');
    const meioPagamentoIndex = columns.indexOf('MeioPagamento');
    const precoTotalComDescontoIndex = columns.indexOf('sum(PrecoTotalComDesconto)');
    const dataAprovadoIndex = columns.indexOf('DataAprovado');
    const dataFaturadoIndex = columns.indexOf('DataFaturado');
    const dataEntregueIndex = columns.indexOf('DataEntregue');
    const dataCanceladoIndex = columns.indexOf('DataCancelado');
    const tipoVendaIndex = columns.indexOf('TipoVenda');
    const nomeVendedorIndex = columns.indexOf('NomeVendedor');

    const orders: Order[] = data.map((row:any, index:any) => {
      const dataAprovado = row[dataAprovadoIndex] as string | null;
      const dataFaturado = row[dataFaturadoIndex] as string | null;
      const dataEntregue = row[dataEntregueIndex] as string | null;
      const dataCancelado = row[dataCanceladoIndex] as string | null;

      const status = determineOrderStatus(
        dataAprovado,
        dataFaturado,
        dataEntregue,
        dataCancelado
      );

      return {
        id: String(row[idPedidoIndex] || index),
        orderNumber: String(row[idPedidoIndex] || ''),
        clientId: String(row[idPedidoIndex] || ''),
        storeId: String(row[idPedidoIndex] || ''),
        storeName: String(row[razaoSocialIndex] || ''),
        sellerId: row[tipoVendaIndex]?.toLowerCase() == 'assistida' ? row[nomeVendedorIndex] : '',
        totalValue: parseFloat(String(row[precoTotalComDescontoIndex] || 0)),
        paymentMethod: String(row[meioPagamentoIndex] || ''),
        status,
        purchasedAt: String(row[dataIndex] || ''),
        paymentApprovedAt: dataAprovado || undefined,
        invoicedAt: dataFaturado || undefined,
        deliveredAt: dataEntregue || undefined,
        canceledAt: dataCancelado || undefined,
      };
    });

    return orders;
  } catch (error) {
    console.error('Error searching orders:', error);
    throw error;
  }
};

export const getOrderDetails = async (idPedido: string): Promise<OrderDetailsData> => {
  try {
    const [response, voucherResponse] = await Promise.all([
      axios.get(`/infrapay/pedido/${idPedido}`),
      axios.get(`/infrapay/voucher/${idPedido}`)
    ]);
    //const response:any = await axios.get(`/infrapay/pedido/${idPedido}`);

    if (!response.data.success) {
      throw new Error(`API error: ${response.status}`);
    }

    const apiResponse: ApiOrderDetailsResponse = response.data.data;

    if (!apiResponse.success) {
      throw new Error('API returned unsuccessful response');
    }

    const { columns, data } = response.data.data;

    if (data.length === 0) {
      throw new Error('Order not found');
    }

    const idPedidoIndex = columns.indexOf('IDPedido');
    const dataIndex = columns.indexOf('Data');
    const razaoSocialIndex = columns.indexOf('RazaoSocial');
    const cnpjLojistaIndex = columns.indexOf('CNPJLojista');
    const meioPagamentoIndex = columns.indexOf('MeioPagamento');
    const dataAprovadoIndex = columns.indexOf('DataAprovado');
    const dataFaturadoIndex = columns.indexOf('DataFaturado');
    const dataEntregueIndex = columns.indexOf('DataEntregue');
    const dataCanceladoIndex = columns.indexOf('DataCancelado');
    const idSkuIndex = columns.indexOf('IDSku');
    const eanIndex = columns.indexOf('EAN');
    const produtoIndex = columns.indexOf('Produto');
    const quantidadeIndex = columns.indexOf('Quantidade');
    const precoTotalSemDescontoIndex = columns.indexOf('PrecoTotalSemDesconto');
    const precoTotalComDescontoIndex = columns.indexOf('PrecoTotalComDesconto');
    const distribuidorIndex = columns.indexOf('Distribuidor');
    const tipoVendaIndex = columns.indexOf('TipoVenda');
    const nomeVendedorIndex = columns.indexOf('NomeVendedor');
    const origemIndex = columns.indexOf('Origem');

    const firstRow = data[0];
    const dataPedido = firstRow[dataIndex] as string | null;
    const dataAprovado = firstRow[dataAprovadoIndex] as string | null;
    const dataFaturado = firstRow[dataFaturadoIndex] as string | null;
    const dataEntregue = firstRow[dataEntregueIndex] as string | null;
    const dataCancelado = firstRow[dataCanceladoIndex] as string | null;

    const status = determineOrderStatus(
      dataAprovado,
      dataFaturado,
      dataEntregue,
      dataCancelado
    );

    let totalValue = 0;
    const items: OrderItem[] = data.map((row:any, index:any) => {
      const precoTotal = parseFloat(String(row[precoTotalComDescontoIndex] || 0));
      const quantidade = parseInt(String(row[quantidadeIndex] || 0));
      totalValue += precoTotal;

      return {
        id: `${row[idPedidoIndex]}-${index}`,
        orderId: String(row[idPedidoIndex] || ''),
        sku: String(row[idSkuIndex] || ''),
        productName: String(row[produtoIndex] || ''),
        quantity: quantidade,
        invoicedQuantity: quantidade,
        unitPrice: quantidade > 0 ? precoTotal / quantidade : 0,
        totalPrice: precoTotal,
        invoicedTotal: precoTotal,
      };
    });

    const order: Order = {
      id: String(firstRow[idPedidoIndex] || ''),
      orderNumber: String(firstRow[idPedidoIndex] || ''),
      clientId: String(firstRow[cnpjLojistaIndex] || ''),
      storeId: String(firstRow[idPedidoIndex] || ''),
      storeName: String(firstRow[razaoSocialIndex] || ''),
      sellerId: String(firstRow[nomeVendedorIndex] || ''),
      totalValue,
      paymentMethod: String(firstRow[meioPagamentoIndex] || ''),
      status,
      purchasedAt: dataPedido || '',
      paymentApprovedAt: dataAprovado || undefined,
      invoicedAt: dataFaturado || undefined,
      deliveredAt: dataEntregue || undefined,
      canceledAt: dataCancelado || undefined,
      origem: firstRow[origemIndex] || undefined,
      distribuidor: firstRow[distribuidorIndex] || undefined
    };

    let vouchers: VoucherInfo[] | undefined;
    if (voucherResponse.data.success) {
      try {
        const voucherData: ApiVoucherResponse = await voucherResponse.data;
        if (voucherData.data.success) {
          const { columns, data: voucherRows } = voucherData.data;
          const additionalInfoIndex = columns.indexOf('additional_info');
          const typeIndex = columns.indexOf('type');
          const createdAtIndex = columns.indexOf('created_at');
          vouchers = voucherRows.map((row:any) => ({
            additional_info: String(row[additionalInfoIndex] || ''),
            type: String(row[typeIndex] || ''),
            created_at: String(row[createdAtIndex] || '')
          }));
        }
      } catch (error) {
        console.error('Error parsing voucher data:', error);
      }
    }
    return {
      order,
      items,
      vouchers,
    };
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};
